import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ScrapingService, ScrapedRestaurant } from '@/services/scraping.service';
import { useAuth } from '@/contexts/AuthContext';

export const ImportLeads: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const [importData, setImportData] = useState('');
  const [format, setFormat] = useState<'csv' | 'json'>('csv');
  const [preview, setPreview] = useState<ScrapedRestaurant[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  const mutation = useMutation({
    mutationFn: async (restaurants: ScrapedRestaurant[]) => {
      if (!profile?.id) throw new Error('User not authenticated');
      return ScrapingService.importRestaurants(restaurants, profile.id);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      alert(`Import complete!\nSuccess: ${result.success}\nFailed: ${result.failed}`);
      if (result.errors.length > 0) {
        setErrors(result.errors);
      } else {
        navigate('/sales');
      }
    },
  });

  const handleParse = () => {
    try {
      const parsed = ScrapingService.parseImportData(importData, format);
      
      // Validate each restaurant
      const valid: ScrapedRestaurant[] = [];
      const validationErrors: string[] = [];
      
      parsed.forEach((restaurant, index) => {
        const validation = ScrapingService.validateRestaurant(restaurant);
        if (validation.valid) {
          valid.push(restaurant);
        } else {
          validationErrors.push(`Row ${index + 1}: ${validation.errors.join(', ')}`);
        }
      });
      
      setPreview(valid);
      setErrors(validationErrors);
    } catch (error: any) {
      setErrors([error.message]);
      setPreview([]);
    }
  };

  const handleImport = () => {
    if (preview.length === 0) {
      alert('Please parse the data first');
      return;
    }
    
    if (!confirm(`Import ${preview.length} leads?`)) {
      return;
    }
    
    mutation.mutate(preview);
  };

  const sampleCSV = `businessName,ownerName,phone,email,website,city,state,zipCode,restaurantType,priceTier,estimatedEmployees,source,sourceUrl
Joe's Diner,Joe Smith,555-0100,joe@joesdiner.com,joesdiner.com,New York,NY,10001,restaurant,$$,11-25,google_maps,https://maps.google.com/...
Maria's Cafe,Maria Garcia,555-0200,,mariascafe.com,Los Angeles,CA,90001,cafe,$,1-10,yelp,https://yelp.com/...`;

  const sampleJSON = JSON.stringify([
    {
      businessName: "Joe's Diner",
      ownerName: "Joe Smith",
      phone: "555-0100",
      email: "joe@joesdiner.com",
      website: "joesdiner.com",
      address: {
        city: "New York",
        state: "NY",
        zipCode: "10001"
      },
      restaurantType: "restaurant",
      priceTier: "$$",
      estimatedEmployees: "11-25",
      source: "google_maps",
      sourceUrl: "https://maps.google.com/..."
    }
  ], null, 2);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Import Restaurant Leads</h1>

      <div className="bg-white shadow rounded-lg p-6 space-y-6">
        {/* Format Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Import Format</label>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                value="csv"
                checked={format === 'csv'}
                onChange={(e) => setFormat(e.target.value as 'csv')}
                className="mr-2"
              />
              CSV
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="json"
                checked={format === 'json'}
                onChange={(e) => setFormat(e.target.value as 'json')}
                className="mr-2"
              />
              JSON
            </label>
          </div>
        </div>

        {/* Data Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Paste {format.toUpperCase()} Data
          </label>
          <textarea
            rows={12}
            value={importData}
            onChange={(e) => setImportData(e.target.value)}
            placeholder={format === 'csv' ? sampleCSV : sampleJSON}
            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
          />
          <p className="mt-2 text-sm text-gray-500">
            Required fields: businessName, city, state, source
          </p>
        </div>

        {/* Parse Button */}
        <div>
          <button
            onClick={handleParse}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Parse & Preview
          </button>
        </div>

        {/* Errors */}
        {errors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <h3 className="text-sm font-medium text-red-800 mb-2">Validation Errors:</h3>
            <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Preview */}
        {preview.length > 0 && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Preview: {preview.length} valid leads
              </h3>
              <button
                onClick={handleImport}
                disabled={mutation.isPending}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {mutation.isPending ? 'Importing...' : `Import ${preview.length} Leads`}
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Business</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employees</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {preview.slice(0, 10).map((restaurant, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3 text-sm text-gray-900">{restaurant.businessName}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {restaurant.address.city}, {restaurant.address.state}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{restaurant.restaurantType || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{restaurant.estimatedEmployees || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{restaurant.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.length > 10 && (
                <p className="mt-2 text-sm text-gray-500 text-center">
                  Showing first 10 of {preview.length} leads
                </p>
              )}
            </div>
          </div>
        )}

        {/* Import Results */}
        {mutation.isSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <h3 className="text-sm font-medium text-green-800 mb-2">Import Complete!</h3>
            <p className="text-sm text-green-700">
              Successfully imported {mutation.data.success} leads.
              {mutation.data.failed > 0 && ` ${mutation.data.failed} failed.`}
            </p>
          </div>
        )}

        {/* Cancel Button */}
        <div className="flex justify-end">
          <button
            onClick={() => navigate('/sales')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-blue-900 mb-4">Scraping Sources Guide</h2>
        <div className="space-y-4 text-sm text-blue-800">
          <div>
            <h3 className="font-medium mb-2">1. Google Maps</h3>
            <p>Search for: "Restaurant", "Bar", "Cafe", "Food truck"</p>
            <p>Filters: 1-3 locations, Not chains, 4+ reviews</p>
          </div>
          <div>
            <h3 className="font-medium mb-2">2. Yelp</h3>
            <p>Use categorization: Independent vs chain, Price tier ($$ / $$$)</p>
          </div>
          <div>
            <h3 className="font-medium mb-2">3. Health Department Licenses</h3>
            <p>Public data: Business name, Owner name, Address, License number</p>
          </div>
          <div>
            <h3 className="font-medium mb-2">4. POS Signals</h3>
            <p>Look for: Toast, Square, Clover (indicates tech-enabled businesses)</p>
          </div>
        </div>
      </div>
    </div>
  );
};

